import React, { useState, useEffect, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

function TicketingSettings({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [config, setConfig] = useState({
    bearer_token: '',
    ticket_create_url: 'https://smartbizcares.com/api/ticket_create',
    image_upload_url: 'https://smartbizcares.com/api/ticket_attachment_upload',
    support_email: '',
    support_mobile: '',
    is_enabled: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [showToken, setShowToken] = useState(false);

  // ── External API Key Management ──
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [newKeyExpires, setNewKeyExpires] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState(['banquet_lead']);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null); // { api_key, secret_key }
  const [apiKeyError, setApiKeyError] = useState('');
  const [apiKeyMsg, setApiKeyMsg] = useState('');
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [showDocs, setShowDocs] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [apiLog, setApiLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [copiedField, setCopiedField] = useState('');

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  useEffect(() => {
    setLoading(true);
    fetch('/api/v1/support/ticketing-config', { headers: headers() })
      .then(r => r.json())
      .then(data => {
        setConfig({
          bearer_token: data.bearer_token || '',
          ticket_create_url: data.ticket_create_url || 'https://smartbizcares.com/api/ticket_create',
          image_upload_url: data.image_upload_url || 'https://smartbizcares.com/api/ticket_attachment_upload',
          support_email: data.support_email || '',
          support_mobile: data.support_mobile || '',
          is_enabled: Boolean(data.is_enabled),
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    loadApiKeys();
  }, [headers]);

  const handleSave = async () => {
    if (!config.bearer_token.trim()) {
      setError('Bearer Token is required');
      return;
    }
    setSaving(true);
    setMsg('');
    setError('');
    try {
      const res = await fetch('/api/v1/support/ticketing-config', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(config),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMsg('Ticketing configuration saved successfully.');
      } else {
        setError(data.error || 'Failed to save');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#475569', marginBottom: 4 };
  const inputStyle = { width: '100%', padding: '0.55rem 0.8rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' };
  const readOnlyStyle = { ...inputStyle, background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' };

  return (
    <div className="page-shell" style={{ maxWidth: 700 }}>
      <h3 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.5rem' }}>EASECARES & External API Settings</h3>
      <p style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: '1.5rem' }}>
        Configure EASECARES (formerly SmartBizCares) ticketing and manage External API keys for 3rd party CRM integrations.
      </p>

      {loading && <div style={{ color: '#6b7280', marginBottom: '1rem' }}>Loading...</div>}
      {msg && <div style={{ padding: '0.6rem 1rem', borderRadius: 8, background: '#dcfce7', color: '#166534', fontSize: '0.85rem', marginBottom: '1rem' }}>{msg}</div>}
      {error && <div style={{ padding: '0.6rem 1rem', borderRadius: 8, background: '#fef2f2', color: '#991b1b', fontSize: '0.85rem', marginBottom: '1rem' }}>{error}</div>}

      <div className="card" style={{ padding: '1.5rem' }}>
        {/* Enable toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', padding: '0.8rem 1rem', background: config.is_enabled ? '#f0fdf4' : '#f9fafb', borderRadius: 8, border: `1px solid ${config.is_enabled ? '#bbf7d0' : '#e5e7eb'}` }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
            <input type="checkbox" checked={config.is_enabled} onChange={e => setConfig({ ...config, is_enabled: e.target.checked })} style={{ width: 18, height: 18 }} />
            Enable Support Ticketing
          </label>
          <span style={{ fontSize: '0.78rem', color: config.is_enabled ? '#166534' : '#94a3b8' }}>
            {config.is_enabled ? 'Users can raise tickets from any screen' : 'Ticketing is disabled for this property'}
          </span>
        </div>

        {/* Provider (read-only) */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Ticketing System</label>
          <input value="EASECARES (formerly SmartBizCares)" readOnly style={readOnlyStyle} />
        </div>

        {/* Bearer Token */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>API Bearer Token <span style={{ color: '#ef4444' }}>*</span></label>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <input
              type={showToken ? 'text' : 'password'}
              value={config.bearer_token}
              onChange={e => setConfig({ ...config, bearer_token: e.target.value })}
              placeholder="Enter SMARTBIZCARES Bearer Token"
              style={{ ...inputStyle, flex: 1 }}
            />
            <button onClick={() => setShowToken(!showToken)} type="button"
              style={{ padding: '0.55rem 0.8rem', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
              {showToken ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        {/* Ticket Create URL */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Ticket Create API URL</label>
          <input
            value={config.ticket_create_url}
            onChange={e => setConfig({ ...config, ticket_create_url: e.target.value })}
            placeholder="https://smartbizcares.com/api/ticket_create"
            style={inputStyle}
          />
        </div>

        {/* Image Upload URL */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Image Upload API URL</label>
          <input
            value={config.image_upload_url}
            onChange={e => setConfig({ ...config, image_upload_url: e.target.value })}
            placeholder="https://smartbizcares.com/api/ticket_attachment_upload"
            style={inputStyle}
          />
        </div>

        {/* Hardcoded values (read-only) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={labelStyle}>Category ID</label>
            <input value="182 (PMS)" readOnly style={readOnlyStyle} />
          </div>
          <div>
            <label style={labelStyle}>Sub-Category ID</label>
            <input value="779 (PMS)" readOnly style={readOnlyStyle} />
          </div>
          <div>
            <label style={labelStyle}>Source</label>
            <input value="PMS" readOnly style={readOnlyStyle} />
          </div>
          <div>
            <label style={labelStyle}>Sub-Source</label>
            <input value="Portal" readOnly style={readOnlyStyle} />
          </div>
        </div>

        {/* Support contact */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <label style={labelStyle}>Support Email</label>
            <input
              type="email"
              value={config.support_email}
              onChange={e => setConfig({ ...config, support_email: e.target.value })}
              placeholder="support@hotel.com"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Support Mobile</label>
            <input
              value={config.support_mobile}
              onChange={e => setConfig({ ...config, support_mobile: e.target.value })}
              placeholder="9840356793"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Save */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={handleSave} disabled={saving}
            style={{
              padding: '0.6rem 1.5rem', borderRadius: 8, border: 'none',
              background: saving ? '#94a3b8' : 'var(--theme-primary-dark)',
              color: '#fff', fontWeight: 700, cursor: saving ? 'wait' : 'pointer', fontSize: '0.9rem',
            }}>
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
      {/* ════════════════════════════════════════════════════ */}
      {/* EXTERNAL API KEY MANAGEMENT                        */}
      {/* ════════════════════════════════════════════════════ */}
      <h3 style={{ color: 'var(--theme-primary-dark)', marginTop: '2rem', marginBottom: '0.5rem' }}>External API Keys</h3>
      <p style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: '1rem' }}>
        Generate API keys for 3rd party CRM systems (e.g., EASECARES) to push banquet leads. The CRM passes the hotel's CM Hotel ID to identify the property.
      </p>

      {apiKeyMsg && <div style={{ padding: '0.6rem 1rem', borderRadius: 8, background: '#dcfce7', color: '#166534', fontSize: '0.85rem', marginBottom: '1rem' }}>{apiKeyMsg}</div>}
      {apiKeyError && <div style={{ padding: '0.6rem 1rem', borderRadius: 8, background: '#fef2f2', color: '#991b1b', fontSize: '0.85rem', marginBottom: '1rem' }}>{apiKeyError}</div>}

      {/* Generate New Key */}
      <div className="card" style={{ padding: '1.2rem', marginBottom: '1rem' }}>
        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a365d', marginBottom: '0.6rem' }}>Generate New API Key</div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: '0.6rem', alignItems: 'end' }}>
          <div>
            <label style={labelStyle}>Key Label <span style={{ color: '#ef4444' }}>*</span></label>
            <input value={newKeyLabel} onChange={(e) => setNewKeyLabel(e.target.value)} placeholder="e.g. EASECARES CRM" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Expires (optional)</label>
            <input type="date" value={newKeyExpires} onChange={(e) => setNewKeyExpires(e.target.value)} style={inputStyle} />
          </div>
          <button onClick={async () => {
            if (!newKeyLabel.trim()) { setApiKeyError('Key label is required'); return; }
            setGeneratingKey(true); setApiKeyError(''); setApiKeyMsg('');
            try {
              const res = await fetch('/api/v1/external/keys', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ key_label: newKeyLabel, scopes: newKeyScopes, expires_at: newKeyExpires || null })
              });
              const data = await res.json();
              if (!res.ok) throw new Error(data?.error || 'Failed to generate key');
              setGeneratedKey({ api_key: data.api_key, secret_key: data.secret_key });
              setNewKeyLabel(''); setNewKeyExpires('');
              loadApiKeys();
            } catch (err) { setApiKeyError(err.message); }
            finally { setGeneratingKey(false); }
          }} disabled={generatingKey}
            style={{ padding: '0.55rem 1rem', borderRadius: 8, border: 'none', background: generatingKey ? '#94a3b8' : '#059669', color: '#fff', fontWeight: 700, cursor: generatingKey ? 'wait' : 'pointer', fontSize: '0.85rem', height: 'fit-content' }}>
            {generatingKey ? 'Generating...' : 'Generate Key'}
          </button>
        </div>
        <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.6rem', fontSize: '0.78rem' }}>
          {['banquet_lead'].map(s => (
            <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={newKeyScopes.includes(s)} onChange={(e) => setNewKeyScopes(e.target.checked ? [...newKeyScopes, s] : newKeyScopes.filter(x => x !== s))} />
              {s}
            </label>
          ))}
        </div>
      </div>

      {/* One-Time Secret Modal */}
      {generatedKey && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', maxWidth: 550, width: '95%' }}>
            <h4 style={{ color: '#1a365d', marginTop: 0, marginBottom: '0.5rem' }}>API Key Generated</h4>
            <div style={{ padding: '0.6rem 0.8rem', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, marginBottom: '1rem', fontSize: '0.82rem', color: '#92400e', fontWeight: 600 }}>
              Save the Secret Key now. It will NOT be shown again.
            </div>
            <div style={{ marginBottom: '0.8rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 2 }}>API Key</div>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <code style={{ flex: 1, padding: '0.5rem', background: '#f1f5f9', borderRadius: 6, fontFamily: 'monospace', fontSize: '0.82rem', wordBreak: 'break-all' }}>{generatedKey.api_key}</code>
                <button onClick={() => { navigator.clipboard.writeText(generatedKey.api_key); setCopiedField('api'); setTimeout(() => setCopiedField(''), 2000); }}
                  style={{ padding: '0.3rem 0.6rem', borderRadius: 6, border: '1px solid #cbd5e0', background: copiedField === 'api' ? '#dcfce7' : '#fff', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                  {copiedField === 'api' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 2 }}>Secret Key</div>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <code style={{ flex: 1, padding: '0.5rem', background: '#fef2f2', borderRadius: 6, fontFamily: 'monospace', fontSize: '0.82rem', wordBreak: 'break-all', color: '#991b1b' }}>{generatedKey.secret_key}</code>
                <button onClick={() => { navigator.clipboard.writeText(generatedKey.secret_key); setCopiedField('secret'); setTimeout(() => setCopiedField(''), 2000); }}
                  style={{ padding: '0.3rem 0.6rem', borderRadius: 6, border: '1px solid #cbd5e0', background: copiedField === 'secret' ? '#dcfce7' : '#fff', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                  {copiedField === 'secret' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <button onClick={() => setGeneratedKey(null)}
              style={{ padding: '0.5rem 1.2rem', borderRadius: 8, border: 'none', background: '#1a365d', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
              I have saved the keys
            </button>
          </div>
        </div>
      )}

      {/* API Keys List */}
      <div className="card" style={{ padding: '1.2rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a365d' }}>Active API Keys</div>
          <button onClick={loadApiKeys} disabled={apiKeysLoading} style={{ padding: '0.3rem 0.6rem', borderRadius: 6, border: '1px solid #cbd5e0', background: '#fff', cursor: 'pointer', fontSize: '0.75rem' }}>
            {apiKeysLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#1a365d' }}>
                <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', color: '#fff', fontWeight: 600 }}>Label</th>
                <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', color: '#fff', fontWeight: 600 }}>API Key</th>
                <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', color: '#fff', fontWeight: 600 }}>Scopes</th>
                <th style={{ padding: '0.35rem 0.5rem', textAlign: 'center', color: '#fff', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', color: '#fff', fontWeight: 600 }}>Last Used</th>
                <th style={{ padding: '0.35rem 0.5rem', textAlign: 'center', color: '#fff', fontWeight: 600 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map(k => (
                <tr key={k.key_id} style={{ borderBottom: '1px solid #e2e8f0', opacity: k.is_active ? 1 : 0.5 }}>
                  <td style={{ padding: '0.35rem 0.5rem', fontWeight: 600 }}>{k.key_label}</td>
                  <td style={{ padding: '0.35rem 0.5rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>{k.api_key?.slice(0, 12)}...
                    <button onClick={() => { navigator.clipboard.writeText(k.api_key); setCopiedField(`k-${k.key_id}`); setTimeout(() => setCopiedField(''), 2000); }}
                      style={{ marginLeft: 4, padding: '1px 5px', borderRadius: 4, border: '1px solid #cbd5e0', background: copiedField === `k-${k.key_id}` ? '#dcfce7' : '#fff', cursor: 'pointer', fontSize: '0.68rem' }}>
                      {copiedField === `k-${k.key_id}` ? 'Copied' : 'Copy'}
                    </button>
                  </td>
                  <td style={{ padding: '0.35rem 0.5rem' }}>
                    {(k.scopes || []).map(s => <span key={s} style={{ display: 'inline-block', background: '#ede9fe', color: '#6d28d9', borderRadius: 4, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 600, marginRight: 3 }}>{s}</span>)}
                  </td>
                  <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700, background: k.is_active ? '#dcfce7' : '#fee2e2', color: k.is_active ? '#166534' : '#991b1b' }}>
                      {k.is_active ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td style={{ padding: '0.35rem 0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                    {k.last_used_at ? new Date(k.last_used_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never'}
                  </td>
                  <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center' }}>
                    {k.is_active && (
                      confirmRevoke === k.key_id ? (
                        <span style={{ fontSize: '0.75rem' }}>
                          Sure? <button onClick={async () => {
                            await fetch(`/api/v1/external/keys/${k.key_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
                            setConfirmRevoke(null); loadApiKeys(); setApiKeyMsg('Key revoked');
                          }} style={{ color: '#ef4444', fontWeight: 700, border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.75rem' }}>Yes</button>
                          <button onClick={() => setConfirmRevoke(null)} style={{ color: '#6b7280', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.75rem' }}>No</button>
                        </span>
                      ) : (
                        <button onClick={() => setConfirmRevoke(k.key_id)} style={{ padding: '2px 8px', borderRadius: 4, border: 'none', background: '#fee2e2', color: '#991b1b', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600 }}>Revoke</button>
                      )
                    )}
                  </td>
                </tr>
              ))}
              {!apiKeys.length && <tr><td colSpan={6} style={{ padding: '0.8rem', color: '#94a3b8', textAlign: 'center' }}>No API keys generated yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* API Documentation */}
      <div className="card" style={{ padding: '1.2rem', marginBottom: '1rem' }}>
        <div onClick={() => setShowDocs(!showDocs)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a365d' }}>API Integration Guide</div>
          <span style={{ color: '#94a3b8' }}>{showDocs ? '\u25B2' : '\u25BC'}</span>
        </div>
        {showDocs && (
          <div style={{ marginTop: '0.8rem', fontSize: '0.82rem' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Endpoint</div>
            <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: '0.6rem', borderRadius: 6, overflow: 'auto', fontSize: '0.78rem', marginBottom: '0.8rem' }}>POST /api/v1/external/leads</pre>

            <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Headers</div>
            <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: '0.6rem', borderRadius: 6, overflow: 'auto', fontSize: '0.78rem', marginBottom: '0.8rem' }}>{`x-api-key: ek_xxxxxxxxxxxx
x-api-secret: sk_xxxxxxxxxxxx
Content-Type: application/json`}</pre>

            <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Sample Request Body</div>
            <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: '0.6rem', borderRadius: 6, overflow: 'auto', fontSize: '0.78rem', marginBottom: '0.8rem' }}>{JSON.stringify({
              hotel_id: "12345",
              client_name: "John Smith",
              client_mobile: "9876543210",
              client_email: "john@example.com",
              client_company: "Smith Corp",
              lead_source: "WEBSITE",
              event_type: "WEDDING",
              event_name: "Smith-Jones Wedding",
              event_from_date: "2026-05-15",
              event_to_date: "2026-05-16",
              expected_pax: 200,
              budget_min: 200000,
              budget_max: 500000,
              is_room_required: true,
              num_rooms: 10,
              food_requirement: "Veg + Non-Veg buffet",
              special_requests: "Stage decoration needed"
            }, null, 2)}</pre>

            <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Field Reference</div>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '0.6rem', marginBottom: '0.8rem', fontSize: '0.78rem' }}>
              <div><b>hotel_id</b> (required): CM Hotel ID from Property Master</div>
              <div><b>client_name</b> (required): Guest / client name</div>
              <div><b>lead_source</b>: WEBSITE, WHATSAPP, PHONE, EMAIL, WALKIN, MARKETPLACE, EVENT_ORGANIZER, OTA, SALES_TEAM, REFERRAL, SOCIAL_MEDIA</div>
              <div><b>event_type</b>: WEDDING, CORPORATE, BIRTHDAY, SOCIAL, MICE, CONFERENCE, EXHIBITION, RECEPTION, ENGAGEMENT, OTHER</div>
              <div>All other fields are optional.</div>
            </div>

            <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Success Response (201)</div>
            <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: '0.6rem', borderRadius: 6, overflow: 'auto', fontSize: '0.78rem' }}>{JSON.stringify({ success: true, lead_id: "uuid", lead_no: "BL-202604-0001", pipeline_stage: "NEW_LEAD", property: "Grand Hotel" }, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* Request Log */}
      <div className="card" style={{ padding: '1.2rem', marginBottom: '2rem' }}>
        <div onClick={() => { setShowLog(!showLog); if (!showLog && !apiLog.length) loadApiLog(); }} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a365d' }}>API Request Log</div>
          <span style={{ color: '#94a3b8' }}>{showLog ? '\u25B2' : '\u25BC'}</span>
        </div>
        {showLog && (
          <div style={{ marginTop: '0.6rem', overflowX: 'auto' }}>
            {logLoading ? <div style={{ color: '#6b7280', padding: '0.5rem' }}>Loading...</div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>Time</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>Key</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>Hotel ID</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>Response</th>
                  </tr>
                </thead>
                <tbody>
                  {apiLog.map(l => (
                    <tr key={l.log_id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '0.25rem 0.4rem', whiteSpace: 'nowrap' }}>{new Date(l.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                      <td style={{ padding: '0.25rem 0.4rem' }}>{l.key_label || '-'}</td>
                      <td style={{ padding: '0.25rem 0.4rem', fontFamily: 'monospace' }}>{l.hotel_id || '-'}</td>
                      <td style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>
                        <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, background: l.response_code < 300 ? '#dcfce7' : '#fee2e2', color: l.response_code < 300 ? '#166534' : '#991b1b' }}>{l.response_code}</span>
                      </td>
                      <td style={{ padding: '0.25rem 0.4rem', color: '#6b7280', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.response_body ? (typeof l.response_body === 'string' ? l.response_body : JSON.stringify(l.response_body)).slice(0, 80) : '-'}
                      </td>
                    </tr>
                  ))}
                  {!apiLog.length && <tr><td colSpan={5} style={{ padding: '0.6rem', color: '#94a3b8', textAlign: 'center' }}>No API requests logged yet</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );

  function loadApiKeys() {
    setApiKeysLoading(true);
    fetch('/api/v1/external/keys', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setApiKeys(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setApiKeysLoading(false));
  }

  function loadApiLog() {
    setLogLoading(true);
    fetch('/api/v1/external/leads/log', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setApiLog(Array.isArray(data) ? data.slice(0, 50) : []))
      .catch(() => {})
      .finally(() => setLogLoading(false));
  }
}

export default TicketingSettings;
